/**
 * Gift with Purchase Script
 * Automatically adds free gift products to cart when order meets specified conditions
 */
import { utils } from '@hh/global.js';
const { reRenderShopifySections } = utils;

document.addEventListener('DOMContentLoaded', function() {
  // Configuration
  const gwpConfig = window.gwpConfig || [];
  // Track gift add attempts to prevent repeated attempts
  const attemptedGiftAdds = {};

  function isProductGift(item, offer) {
    return (
      item.variant_id.toString() === offer?.id && 
      item.properties._gift === 'true'
      // item.price < 1
    )
  }

  // Check cart and manage gifts if conditions are met
  async function checkAndManageGifts() {
    const response = await fetch('/cart.js')
    const cart = await response.json();
    let rerenderRequired = false;
      
    // Process each gift offer
    for (const offer of gwpConfig) {
      const giftInCart = cart.items.find(item => isProductGift(item, offer));

      if (isCartEligibleForOffer(cart, offer) && !giftInCart) {
        // Skip if we've already attempted to add this gift and it failed
        if (attemptedGiftAdds[offer.id]) {
          console.log('Skipping gift add attempt - already tried:', offer);
          continue;
        }
        
        console.log('Adding gift to cart', offer);
        await addGiftToCart(offer);
        rerenderRequired = true;
      } 

      if (!isCartEligibleForOffer(cart, offer) && giftInCart) {
        await removeGiftFromCart(cart, offer);
        // Reset the attempt tracking when we remove the gift
        attemptedGiftAdds[offer.id] = false;
        rerenderRequired = true;
      }
    }

    if (rerenderRequired) {
      const response = await fetch(`/?sections=mini-cart,header`)
      const json = await response.json();
      reRenderShopifySections({sections: json});
    }
  }

  // Determine if we should add the gift based on cart contents and offer rules
  function isCartEligibleForOffer(cart, offer) {
    // Check if cart has other items besides this gift
    const nonGiftItems = cart.items.filter(item => !isProductGift(item, offer));
    if (nonGiftItems.length === 0) {
      return false;
    }
    
    // Calculate eligible cart total (excluding gift product and excluded products)
    const eligibleTotal = calculateEligibleTotal(cart, offer);
    
    // Check if total meets threshold
    if (eligibleTotal < offer.minCartTotal) {
      return false;
    }
    
    return true;
  }

  // Calculate cart total excluding specified products for a specific offer
  function calculateEligibleTotal(cart, offer) {
    let total = cart.items.reduce((sum, item) => {
      // Skip any gift products
      if (gwpConfig.some(config => isProductGift(item, config))) {
        return sum;
      }

      return sum + item.line_price;
    }, 0);
    
    return total;
  }

  // Add gift product to cart
  async function addGiftToCart(offer) {
    // Mark this gift as attempted
    attemptedGiftAdds[offer.id] = true;
    
    const data = {
      id: offer.id,
      quantity: 1,
      properties: {
        '_gift': 'true'
      },
      sections: 'mini-cart,header'
    };
    
    return await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    })
  }
  
  // Remove gift product from cart if it exists
  async function removeGiftFromCart(cart, offer) {
    const giftItem = cart.items.find(item => isProductGift(item, offer));
    
    // If gift is in cart, remove it
    if (giftItem) {
      const data = {
        updates: {
          [giftItem.key]: 0
        },
        sections: 'mini-cart,header'
      };
      
      return await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      })
    }
  }
  
  // Check on page load
  checkAndManageGifts();
  
  // Listen for AJAX cart adds
  const originalFetch = window.fetch;
  window.fetch = function() {
    const result = originalFetch.apply(this, arguments);
    
    // If this is a cart add/update/change request
    if (arguments[0].includes('/cart/add') || 
        arguments[0].includes('/cart/update') || 
        arguments[0].includes('/cart/change')) {
      result.then(() => setTimeout(checkAndManageGifts, 500));
    }
    
    return result;
  };
});
